import { MenuItem, Stack } from '@mui/material';
import { ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMTextField } from '../MHM/MHMTextField';
import { MHMPasswordField } from '../MHM/MHMPasswordField';
import { MHMButton } from '../MHM/MHMButton';
import { ToggleForgotPasswordForm } from './ToggleForgotPasswordForm';
import { FormEvent, useEffect, useState } from 'react';
import { auth_controller } from '../../../controllers/auth_controller';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { selectedDeviceStateAtom } from '../../../models/atoms/device_atoms';
import { navStateAtom } from '../../../models/atoms/ui_atoms';
import { authStateAtom } from '../../../models/atoms/auth_atoms';
import { useMsal } from '@azure/msal-react';
import MicrosoftIcon from '../../../assets/ms-symbollockup_signin_dark.svg?react';
import { MHMSelect } from '../MHM/MHMSelect';

type Props = {
  onForgotPassword(): void;
};

type MSALProps = {
  onStartLogin(): void;
  onFinishLogin(): void;
};

function MSALProtectedComponent({ onStartLogin, onFinishLogin }: MSALProps) {
  const { instance, accounts, inProgress } = useMsal();
  const [startLogin, setStartLogin] = useState(false);

  useEffect(() => {
    if (!startLogin) {
      return;
    }
    onStartLogin();
    auth_controller
      .microsoft_login(instance, accounts, inProgress)
      .then(result => {
        if (result) {
          setStartLogin(false);
          onFinishLogin();
        }
      })
      .catch(() => {
        setStartLogin(false);
        onFinishLogin();
      });
  }, [instance, accounts, inProgress, startLogin, onStartLogin, onFinishLogin]);

  return (
    <MicrosoftIcon
      onClick={() => {
        setStartLogin(true);
      }}
      style={{ cursor: 'pointer', width: 'fit-content' }}
    />
  );
}

export function LoginForm({ onForgotPassword }: Props) {
  const { t } = useTranslation();
  const setSelectedDevice = useSetRecoilState(selectedDeviceStateAtom);
  const setNavState = useSetRecoilState(navStateAtom);
  const [auth, setAuth] = useRecoilState(authStateAtom);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // TODO: use react-router to handle these
  const searchParams = new URLSearchParams(window.location.search);
  const isFromResetPassword = searchParams.get('from') === 'reset_password';
  function clearParams() {
    window.history.replaceState(null, '', window.location.pathname);
  }

  function resetInvalidState() {
    // skip set state if already set to false to prevent unnecessary calls to /validate_token
    if (!auth.showInvalidCredWarning) {
      return;
    }

    setAuth(prev => ({
      ...prev,
      showInvalidCredWarning: false,
    }));
  }

  async function handleLogin(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isLoggingIn) {
      return;
    }

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email')?.toString().toLowerCase();
    const password = formData.get('password')?.toString();
    if (!email || !password) {
      setAuth(prev => ({ ...prev, showInvalidCredWarning: true }));
      return;
    }

    setIsLoggingIn(true);
    try {
      await auth_controller.get_auth_key(email, password);
      clearParams();
      setIsLoggingIn(false);
      setSelectedDevice(null);
      setNavState('device-metrics');
    } catch (error) {
      console.error(error);
      setIsLoggingIn(false);
    }
  }

  return (
    <Stack
      component="form"
      onSubmit={handleLogin}
      justifyContent="center"
      alignItems="center"
      gap={2}
      width="100%"
    >
      {isFromResetPassword ? (
        <MHMTypography variant="h6" component="p" textAlign="center">
          {t(
            'Password successfully reset. Please login using your new credentials',
          )}
        </MHMTypography>
      ) : null}

      <MHMSelect
        value={
          window.location.hostname.includes('nyc.manhole-metrics.com')
            ? 'https://nyc.manhole-metrics.com'
            : 'https://porthole.manhole-metrics.com'
        }
        onChange={e => window.location.assign(e.target.value)}
        size="small"
        fullWidth
        displayEmpty
        sx={{ bgcolor: 'background.paper' }}
      >
        <MenuItem value="https://porthole.manhole-metrics.com">
          {t('Default')}
        </MenuItem>
        <MenuItem value="https://nyc.manhole-metrics.com">{t('NYC')}</MenuItem>
      </MHMSelect>

      <MHMTextField
        name="email"
        placeholder={t('email')}
        fullWidth
        onChange={resetInvalidState}
        error={auth.showInvalidCredWarning}
        sx={{ bgcolor: 'background.paper' }}
      />
      <MHMPasswordField
        name="password"
        placeholder={t('password')}
        fullWidth
        onChange={resetInvalidState}
        error={auth.showInvalidCredWarning}
        helperText={
          auth.showInvalidCredWarning ? (
            <ErrorText component="span">{t('Invalid Credentials!')}</ErrorText>
          ) : undefined
        }
      />

      <ToggleForgotPasswordForm onClick={onForgotPassword}>
        {t('Forgot Password')}
      </ToggleForgotPasswordForm>

      <MHMButton
        sx={{ width: 'fit-content' }}
        type="submit"
        size="large"
        loading={isLoggingIn}
      >
        {t('Login')}
      </MHMButton>

      <MSALProtectedComponent
        onStartLogin={() => setIsLoggingIn(true)}
        onFinishLogin={() => setIsLoggingIn(false)}
      />
    </Stack>
  );
}
